import { AnalyticsWidgetUiDetailsDto } from "../../types/analytics";
import { UsageDistributionSegment } from "../../types/analytics/UsageDistributionSegment";
import { PieChartDataTransformer } from "../../types/analytics/charts/PieCharts";
import {
  UsageStatusDataset,
  UsageStatusDatasetItem,
} from "../../types/analytics/UsageStatusDataset";
import { chartConstants } from "../../components/analytics/charts/ChartConstants";

const DROPOUTS_KEY = "DROPOUTS";
export const NOT_STARTED_KEY = "NOT-STARTED";
export const PARTIALLY_COMPLETE_KEY = "PARTIALLY-COMPLETE";
export const WAITING_FOR_EMPLOYEE_KEY = "WAITING-FOR-EMPLOYEE";
export const WAITING_FOR_MANAGER_KEY = "WAITING-FOR-MANAGER";
export const COMPLETED_KEY = "COMPLETED";

/** Palette tokens from chartConstants, keyed by meaning rather than dataset order. */
const usageStatusColors: Record<string, string> = {
  [DROPOUTS_KEY]: chartConstants.coloursArray[10],
  [NOT_STARTED_KEY]: chartConstants.coloursArray[4],
  [PARTIALLY_COMPLETE_KEY]: chartConstants.coloursArray[1],
  [WAITING_FOR_EMPLOYEE_KEY]: chartConstants.coloursArray[2],
  [WAITING_FOR_MANAGER_KEY]: chartConstants.coloursArray[0],
  [COMPLETED_KEY]: chartConstants.coloursArray[3],
};

export const getUsageStatusColor = (statusKey: string): string =>
  usageStatusColors[statusKey] ?? chartConstants.coloursArray[0];

export const getCompletionRate = (
  completedCount: number,
  assignedCount: number,
): number => {
  if (assignedCount <= 0) return 0;
  return Math.round((completedCount / assignedCount) * 100);
};

export const formatUsageCount = (value: number): string =>
  value.toLocaleString();

export const getCompletedSegmentColor = (
  _items?: UsageStatusDatasetItem[],
): string => getUsageStatusColor(COMPLETED_KEY);

const getCount = (dataset: UsageStatusDataset, status: string): number =>
  Number(dataset.items?.find((item) => item.label === status)?.value ?? 0);

export const getUsageStatusHeadlineCounts = (dataset: UsageStatusDataset) => {
  const dropoutCount = getCount(dataset, "DROPOUTS");
  const notStartedCount = getCount(dataset, "NOT-STARTED");
  const partiallyCompleteCount = getCount(dataset, "PARTIALLY-COMPLETE");
  const waitingForEmployeeCount = getCount(dataset, "WAITING-FOR-EMPLOYEE");
  const waitingForManagerCount = getCount(dataset, "WAITING-FOR-MANAGER");
  const completedCount = getCount(dataset, "COMPLETED");
  const inProgressCount =
    partiallyCompleteCount + waitingForEmployeeCount + waitingForManagerCount;

  return {
    assignedCount: notStartedCount + inProgressCount + completedCount,
    completedCount,
    dropoutCount,
    inProgressCount,
    notStartedCount,
  };
};

export const buildDistributionSegments = (
  widget: AnalyticsWidgetUiDetailsDto,
): UsageDistributionSegment[] => {
  const rawItems = (widget.datasets?.[0]?.items ?? []) as UsageStatusDatasetItem[];
  const transformer = new PieChartDataTransformer();
  const pieItems = transformer.transformData(widget);

  return pieItems
    .filter((item) => item.value > 0 && item.id !== DROPOUTS_KEY)
    .map((item) => {
      const colorIndex = rawItems.findIndex(
        (rawItem) => rawItem.label === item.id,
      );
      const safeIndex = colorIndex >= 0 ? colorIndex : 0;
      return {
        id: item.id,
        displayLabel: item.label,
        value: item.value,
        color: getUsageStatusColor(item.id),
        colorIndex: safeIndex,
      };
    });
};

export const getDistributionSegmentTotal = (
  segments: UsageDistributionSegment[],
): number => segments.reduce((sum, segment) => sum + segment.value, 0);

/**
 * "Partially complete" means the employee has started but not submitted, so from
 * a chase-work point of view it is the same bucket as waiting on the employee.
 * The summary card shows the four buckets people actually act on, while the
 * underlying data keeps the statuses separate.
 */
export const foldPartiallyCompleteIntoWaitingForEmployee = (
  segments: UsageDistributionSegment[],
  waitingForEmployeeFallbackLabel: string,
): UsageDistributionSegment[] => {
  const partiallyComplete = segments.find(
    (segment) => segment.id === PARTIALLY_COMPLETE_KEY,
  );

  if (!partiallyComplete) {
    return segments;
  }

  const hasWaitingForEmployee = segments.some(
    (segment) => segment.id === WAITING_FOR_EMPLOYEE_KEY,
  );

  return segments.reduce<UsageDistributionSegment[]>((merged, segment) => {
    if (segment.id === PARTIALLY_COMPLETE_KEY) {
      // Keep the partials in place when there is no employee bucket to merge into,
      // relabelled so the reader still sees the four expected statuses.
      if (!hasWaitingForEmployee) {
        merged.push({
          ...segment,
          id: WAITING_FOR_EMPLOYEE_KEY,
          displayLabel: waitingForEmployeeFallbackLabel,
          color: getUsageStatusColor(WAITING_FOR_EMPLOYEE_KEY),
        });
      }

      return merged;
    }

    if (segment.id === WAITING_FOR_EMPLOYEE_KEY) {
      merged.push({
        ...segment,
        value: segment.value + partiallyComplete.value,
      });

      return merged;
    }

    merged.push(segment);
    return merged;
  }, []);
};

/** Rounded share of the total per segment id, keyed so the bar and the cards cannot drift apart. */
export const getSegmentPercentages = (
  segments: UsageDistributionSegment[],
): Record<string, number> => {
  const total = getDistributionSegmentTotal(segments);

  if (total <= 0) {
    return {};
  }

  return segments.reduce<Record<string, number>>((percentages, segment) => {
    percentages[segment.id] = Math.round((segment.value / total) * 100);
    return percentages;
  }, {});
};
